import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../lib/auth-context';
import { useLocale, useT } from '../i18n/locale-context';
import { fetchPaymentHistory } from '../lib/billing-client';
import { formatBudget, formatDeadline } from '../lib/format';

const HISTORY_QUERY_KEY = ['billing', 'history'] as const;
/** Rows shown before the «показать все» expander. */
const PREVIEW_COUNT = 5;

/** Human label for a payment gateway shown in the history list. */
function methodLabel(method: string): string {
  if (method === 'payme') return 'Payme';
  // NOT «Uzcard / Humo»: Octobank is the gateway that clears Visa and
  // Mastercard — the recovery offered when Payme refuses exactly those — so the
  // domestic-scheme label told a buyer who had just paid with a Mastercard that
  // he had paid with a Uzcard. Name the gateway, as the Payme and Click rows do.
  if (method === 'octo') return 'Octobank';
  if (method === 'click') return 'Click';
  // Kazakhstan: every order is a Stripe subscription payment (`stripe-subscribe`,
  // reported as `stripe`) — the processor's name, not the raw key.
  if (method === 'stripe') return 'Stripe';
  return method;
}

/**
 * Payment history for a signed-in paid subscriber. It's reference, not an action,
 * so it renders BELOW the plans grid (the actionable card/auto-renew controls sit
 * in `SubscriptionManagement` above the grid). Most recent first, capped to
 * PREVIEW_COUNT with a «показать все» expander so a long ledger never buries the page.
 */
export function PaymentHistory() {
  const t = useT();
  const { locale } = useLocale();
  const { status, user } = useAuth();
  const [expanded, setExpanded] = useState(false);

  const hasActivePaidSub = Boolean(user?.subscription && !user.subscription.expired);
  const enabled = status === 'authed' && hasActivePaidSub;

  const { data: history } = useQuery({
    queryKey: HISTORY_QUERY_KEY,
    queryFn: fetchPaymentHistory,
    enabled,
  });

  if (!enabled || !history || history.length === 0) return null;

  const shown = expanded ? history : history.slice(0, PREVIEW_COUNT);
  const hasMore = history.length > PREVIEW_COUNT;

  return (
    <section className="mt-8 rounded-xl border border-edge bg-bone-surface p-5 shadow-m-sm sm:p-6">
      <h2 className="mb-3 text-[15px] font-semibold text-ink">{t('billing.history.title')}</h2>
      <ul className="flex flex-col divide-y divide-edge">
        {shown.map((item) => (
          <li key={item.id} className="flex items-center justify-between gap-3 py-2.5">
            <div className="min-w-0">
              <div className="text-[13px] font-medium text-ink">
                {formatDeadline(item.date, locale)}
              </div>
              <div className="text-[11.5px] text-ink-muted">{methodLabel(item.method)}</div>
              {/* The buyer's official fiscal cheque (ofd.soliq.uz), when OCTO
                  issued one — the OCTO cabinet never surfaces these, so this link
                  is the only in-product way to reach it. */}
              {item.fiscalReceiptUrl && (
                <a
                  href={item.fiscalReceiptUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-0.5 inline-block text-[11.5px] font-medium text-brand transition-colors hover:text-brand-mid hover:underline"
                >
                  {t('billing.history.fiscalReceipt')}
                </a>
              )}
            </div>
            {/* Amount only — every row here is a completed payment, so a status
                label would always read «Оплачено» (redundant). */}
            <span className="shrink-0 text-[13px] font-semibold text-ink">
              {formatBudget(String(item.amountUzs))} {t('plans.som')}
            </span>
          </li>
        ))}
      </ul>
      {hasMore && !expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="mt-3 text-[13px] font-semibold text-brand transition-colors hover:text-brand-mid"
        >
          {t('billing.history.showAll', { count: history.length })}
        </button>
      )}
    </section>
  );
}
